/**
 * Configuração de Banners - Multpex Front Gestão
 *
 * Este arquivo contém as configurações dos banners que serão exibidos
 * na home do sistema. Os banners podem ser configurados por tenant
 * ou de forma global.
 */

export const BANNER_VERSION = "1.0.4"; // Versão dos banners (alterar para forçar reexibição)

export const BANNER_CONFIG = {
  // Configurações globais
  global: {
    autoSlide: true,
    slideInterval: 5000, // 5 segundos
    showIndicators: true,
    showCloseButton: true,
    responsive: true,
  },

  // Banners por tenant/cliente
  tenants: {
    // Exemplo para cliente Acciona
    acciona: {
      banners: [
        {
          id: "acciona-banner-1",
          version: "1.1",
          title: "Bem-vindo à Acciona",
          description: "Descubra nossas soluções inovadoras",
          image: require("@/assets/acciona.png"),
          buttonText: "Saiba Mais",
          buttonAction: {
            type: "url",
            value: "https://multpex.com/acciona",
          },
          priority: 1,
          active: false,
          validFrom: "2024-01-01",
          validTo: "2025-12-31",
        },
      ],
    },
    "staging-multpex": {
      banners: [
        {
          id: "staging-multpex-banner-1",
          version: "1.2",
          title: "Armários Inteligentes",
          description:
            "Conheça essa ferramenta inovadora que a Multpex pode te oferecer!",
          image:
            "https://multpex-docs.s3.us-east-1.amazonaws.com/vendas/banner-Multpex.png",
          buttonText: "Saiba Mais",
          buttonAction: {
            type: "url",
            value:
              "mailto:vendas@multpex.com.br?subject=Quero%20saber%20mais%20sobre%20os%20armários&body=Olá%20Multpex,%20gostaria%20de%20receber%20mais%20informações%20sobre%20os%20armários%20inteligentes.",
          },
          priority: 1,
          active: false,
          validFrom: "2024-01-01",
          validTo: "2025-12-31",
        },
      ],
    },

    // Exemplo genérico para outros clientes
    default: {
      banners: [
        {
          id: "multpex-banner-1",
          version: "1.3",
          title: "NOVIDADES MULTPEX",
          description:
            "Fique por dentro das últimas atualizações da plataforma",
          image:
            "https://multpex-docs.s3.us-east-1.amazonaws.com/vendas/BANNER-CRESCE.jpeg",
          buttonText: "Ver Novidades",
          buttonAction: {
            type: "route",
            value: "/novidades",
          },
          priority: 1,
          active: false,
          validFrom: "2024-01-01",
          validTo: "2025-12-31",
        },
        {
          id: "multpex-banner-2",
          version: "1.4",
          title: "Suporte Técnico",
          description: "Nossa equipe está pronta para ajudar você",
          image:
            "https://multpex-docs.s3.us-east-1.amazonaws.com/vendas/BANNER-1400x480.png",
          buttonText: "Contatar Suporte",
          buttonAction: {
            type: "function",
            value: "openSupport",
          },
          priority: 3,
          active: false,
          validFrom: "2024-01-01",
          validTo: "2025-12-31",
        },
      ],
    },
  },

  // Banners globais (exibidos para todos os clientes)
  globalBanners: [
    {
      id: "global-banner-vendas-1",
      version: "1.0",
      image:
        "https://multpex-docs.s3.us-east-1.amazonaws.com/vendas/banner-1-1200x300-.+(2).png",
      buttonText: "Saiba Mais",
      buttonAction: {
        type: "url",
        value:
          "https://www.multpex.com.br/",
      },
      priority: 0,
      active: true,
      validFrom: "2024-01-01",
      validTo: "2026-12-31",
    },
    {
      id: "staging-multpex-banner-2",
      version: "1.1",
      title: "Facial em Tudo",
      description:
        "Agora oferecemos reconhecimento facial em todos os nossos produtos!",
      image:
        "https://multpex-docs.s3.us-east-1.amazonaws.com/vendas/banner-facial.png",
      buttonText: "Saiba Mais",
      buttonAction: {
        type: "url",
        value:
          "https://wa.me/5515997209125?text=Olá%20Multpex,%20gostaria%20de%20receber%20mais%20informações%20sobre%20o%20reconhecimento%20facial.",
      },
      priority: 2,
      active: true,
      validFrom: "2024-01-01",
      validTo: "2025-12-31",
    },
    {
      id: "staging-multpex-banner-1",
      version: "1.2",
      title: "Armários Inteligentes",
      description:
        "Conheça essa ferramenta inovadora que a Multpex pode te oferecer!",
      image:
        "https://multpex-docs.s3.us-east-1.amazonaws.com/vendas/banner-Multpex-armarios.png",
      buttonText: "Saiba Mais",
      buttonAction: {
        type: "url",
        value:
          "mailto:vendas@multpex.com.br?subject=Quero%20saber%20mais%20sobre%20os%20armários&body=Olá%20Multpex,%20gostaria%20de%20receber%20mais%20informações%20sobre%20os%20armários%20inteligentes.",
      },
      priority: 1,
      active: true,
      validFrom: "2024-01-01",
      validTo: "2025-12-31",
    },
  ],
};

/**
 * Serviço de configuração de banners
 */
export const bannerConfig = {
  /**
   * Obtém a configuração de banners para um cliente específico
   * @param {string} clienteId - ID do cliente/tenant
   * @returns {Array} Lista de banners válidos para o cliente
   */
  getBannersForClient(clienteId = "default") {
    const tenantConfig =
      BANNER_CONFIG.tenants[clienteId] || BANNER_CONFIG.tenants.default;

    const allBanners = [
      ...tenantConfig.banners,
      ...BANNER_CONFIG.globalBanners.filter((banner) => banner.active),
    ];

    return allBanners.sort((a, b) => (a.priority || 0) - (b.priority || 0));
  },

  /**
   * Obtém as configurações globais
   * @returns {Object} Configurações globais do carrossel
   */
  getGlobalSettings() {
    return BANNER_CONFIG.global;
  },

  /**
   * Obtém todos os tenants disponíveis
   * @returns {Array} Lista de tenants configurados
   */
  getAvailableTenants() {
    return Object.keys(BANNER_CONFIG.tenants);
  },
};

export default bannerConfig;
